<template>
  <div>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAll"></b-form-checkbox>
        <span @click="checkedAll = !checkedAll">Czas w Las</span>
        <div style="float: right;">
          <img @click="expanded = !expanded" style="height: 13px; cursor: pointer;" :src="expanded ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expanded">
        <b-form-checkbox class="display-inline marginright5" v-model="active"></b-form-checkbox>
        <span @click="active = !active">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="inactive"></b-form-checkbox>
        <span @click="inactive = !inactive">Inactive</span>
      </div>
    </div>
    <MapPlace v-for="place of $store.state.maplasPlaces[$DataOrigin.czaswlas]" :key="place.id" :place="place"></MapPlace>
  </div>
</template>

<script lang="ts">
import BaseComponent from './Base.vue';
import { EventBus } from '@/ts/EventBus';
import Place from '@/ts/Place';
import Photo from '@/ts/Photo';
import PlaceType from '@/ts/PlaceType';
import { AlertStatus, DataOrigin, DownloadStatus } from '@/ts/types';
import { defineComponent } from "vue";

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      checkedAll: false,
      expanded: false,
      active: true,
      inactive: false,
      loading: false
    };
  },
  created() {
    EventBus.$on('downloadExternalData', () => this.downloadPlacesCzasWLas());
    EventBus.$on('toggleCzasWLas', () => {
      if (this.$options.result) {
        this.checkedAll = !this.checkedAll
      }
    });
    EventBus.$on('selectedAreaChanged', (newArea: string, oldArea: string) => this.onSelectedAreaChanged(newArea, oldArea));
  },
  methods: {
    isPointInSelectedArea(lat: number | string, lon: number | string) {
      // @ts-ignore
      const areaKey = window.selectedArea;
      if (!areaKey) return true;
      // @ts-ignore
      const area = window.areas[areaKey];
      if (!area || !area[0] || !area[1]) return true;
      const minLat = Math.min(area[0][0], area[1][0]);
      const maxLat = Math.max(area[0][0], area[1][0]);
      const minLon = Math.min(area[0][1], area[1][1]);
      const maxLon = Math.max(area[0][1], area[1][1]);
      const plat = Number(lat);
      const plon = Number(lon);
      if (Number.isNaN(plat) || Number.isNaN(plon)) return false;
      return (plat >= minLat && plat <= maxLat && plon >= minLon && plon <= maxLon);
    },

    onSelectedAreaChanged(newArea: string, oldArea: string) {
      // remove existing markers/polygons
      if (this.$options.places) {
        for (const place of this.$options.places) {
          try { place.getMarker().removeFrom(window.mainmap); } catch (e) {}
        }
      }
      // reprocess results if available
      if (this.$options.result) {
        this.processPlacesCzasWLas();
      }
    },
    downloadPlacesCzasWLas() {
      this.loading = true;
      const endPoint = window.apiHost + 'api/stringfield/czaswlas/';
      this.downloadUrlWithCache(endPoint, 'czaswlas', 'czaswlas.txt', null, true).then((response) => {
        this.$options.result = response[1].results;
        if (response[0] == DownloadStatus.cache_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.api_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesDownloaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.cache_last) {
          this.createAlert(AlertStatus.warning, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
      }).catch((error) => {
        this.createAlert(AlertStatus.danger, this.$t('placesError').toString(), 2000);
      }).finally(() => {
        this.loading = false;
      });
    },
    processPlacesCzasWLas() {
      const places: Place[] = [];
      const parking_type = new PlaceType(1000000, 'parking', { 'color': 'rgb(38, 120, 252)' }, 1);
      const bicycle_parking_type = new PlaceType(1000000, 'parking', { 'color': 'rgb(114, 128, 219)' }, 1);
      const shelter_type = new PlaceType(1000001, 'other', { 'color': 'brown' }, 1);
      const other_type = new PlaceType(1000002, 'other', { 'color': 'orange' }, 1);
      const picnic_site_type = new PlaceType(1000002, 'other', { 'color': 'green' }, 1);
      const picnic_table_type = new PlaceType(1000002, 'other', { 'color': 'rgb(16, 194, 63)' }, 1);
      const viewpoint_type = new PlaceType(1000002, 'other', { 'color': 'rgb(227, 64, 205)' }, 1);
      const trail_type = new PlaceType(1000002, 'other', { 'color': 'black' }, 1);
      for (const responsePlace of this.$options.result) {
        let placetype = other_type;
        if ([8].includes(responsePlace.category)) {
          placetype = parking_type;
        }
        if ([4, 7, 12, 13, 14, 16].includes(responsePlace.category)) {
          placetype = picnic_site_type;
        }
        if ([2, 3, 15, 20].includes(responsePlace.category)) {
          placetype = picnic_table_type;
        }
        if ([1, 5, 11, 21].includes(responsePlace.category)) {
          placetype = shelter_type;
        }
        if ([17].includes(responsePlace.category)) {
          placetype = viewpoint_type;
        }
        if ([0, 9].includes(responsePlace.category)) {
          placetype = bicycle_parking_type;
        }
        if ([6, 10, 24, 25, 26, 27].includes(responsePlace.category)) {
          placetype = trail_type;
        }
        if ([18, 19, 22, 23, 28].includes(responsePlace.category)) {
          placetype = other_type;
        }
        const place = new Place(responsePlace['id'], responsePlace.name, '', this.roundNumber(responsePlace.lat, 6), this.roundNumber(responsePlace.lon, 6), placetype, false, JSON.parse(responsePlace['waypoints']), DataOrigin.czaswlas, responsePlace.is_active);
        place.createMarker();
        place.link = 'https://czaswlas.pl' + responsePlace.link;
        for (const responsePhoto of responsePlace.images) {
          const photo = new Photo(responsePhoto['full'], 'Photo', '', responsePhoto['thumb'], null, window.mediaHost + 'czaswlas/' + responsePhoto['full'], window.mediaHost + 'czaswlas/' + responsePhoto['full'], window.mediaHost + 'czaswlas/' + responsePhoto['thumb'], false, 1, false);
          place.addPhoto(photo);
        }
        // filter by selected area
        if (this.isPointInSelectedArea(place.lat, place.lon)) {
          places.push(place);
        }
      }
      this.$options.places = places;
      window.externalPlaces.czaswlas = this.$options.places;
      this.onCheckedAllChanged();
    },
    onCheckedAllChanged() {
      if (this.$options.places) {
        for (const place of this.$options.places) {
          if (this.checkedAll) {
            if (place.is_active) {
              if (this.active) {
                place.getMarker().addTo(window.mainmap);
              } else {
                place.getMarker().removeFrom(window.mainmap);
              }
            } else {
              if (this.inactive) {
                place.getMarker().addTo(window.mainmap);
              } else {
                place.getMarker().removeFrom(window.mainmap);
              }
            }
          } else {
            place.getMarker().removeFrom(window.mainmap);
          }
        }
      } else {
        this.processPlacesCzasWLas();
      }
    }
  },
  watch: {
    "checkedAll": [{
      handler: "onCheckedAllChanged"
    }],
    "active": [{
      handler: "onCheckedAllChanged"
    }],
    "inactive": [{
      handler: "onCheckedAllChanged"
    }]
  }
})


</script>